import {
  autoSaveService,
  OneBlinkAppsError,
  submissionService,
} from '@oneblink/apps-react'
import { OneBlinkAutoSaveForm } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'
import { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import Dialog, {
  CloseIcon,
  DialogHeading,
  DialogHeadingText,
} from '../../../components/Dialog'
import ErrorMessage from '../../../components/ErrorMessage'
import LoadingSpinner from '../../../components/LoadingSpinner'
import useCancelForm from '../../../hooks/useCancelForm'
import useSubmitForm from '../../../hooks/useSubmitForm'
import useSaveDraft from '../../../hooks/useSaveDraft'
import useOneBlinkError from '../../../hooks/useOneBlinkError'
import useQuery from '../../../hooks/useQuery'
import useIsDraftInApprovals from '../../../hooks/useIsDraftInApprovals'
import useAutoSaveKey from '../../../hooks/useAutoSaveKey'
import useGoogleAnalyticsFormBeforeUnload from '../../../hooks/useGoogleAnalyticsFormBeforeUnload'
import { useAppConfig } from '../../../providers/AppConfigProvider'
import { postRequest } from '../../../lib/http'
import serverValidation from '../../../lib/server-validation'
import { saveDraft } from '../../../lib/drafts'
import { removeInfoElements } from '../../../lib/infoElementHelpers'
import config from '../../../config'

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 1rem;
  margin-bottom: 1rem;
  margin-right: auto;
`
ButtonContainer.displayName = 'ButtonContainer'

type Props = { form: FormTypes.Form; preFillData: Record<string, unknown> }

function ActivityReport({ form, preFillData }: Props) {
  const autoSaveKey = useAutoSaveKey(preFillData)
  const [
    { onSubmit, clearServerValidationMessage },
    { isSubmitting, serverValidationMessage },
  ] = useSubmitForm({
    formDefinition: form,
    preFillData,
    skipServerValidation: false,
  })
  const { buttons, recaptchaKeyType, recaptchaPublicKey } = useAppConfig()
  const [onCancel, cancelError] = useCancelForm(form)
  const [standardSaveDraft, standardIsSavingDraft] = useSaveDraft({
    formDefinition: form,
    preFillData,
  })

  const [
    { isSavingDraft, invalidDraftMessage, draftSubmission },
    setDraftState,
  ] = useState<{
    isSavingDraft: boolean
    invalidDraftMessage?: string
    draftSubmission?: submissionService.NewDraftSubmission
  }>({ isSavingDraft: false })
  const [saveDraftError, setSaveDraftError] = useOneBlinkError()
  const history = useHistory()
  const { trackingCode: qsTrackingCode } = useQuery()
  const existingTrackingCode = qsTrackingCode as string
  const [{ isFetching }, fetchIsDraftInApprovals] = useIsDraftInApprovals()
  const onBeforeUnload = useGoogleAnalyticsFormBeforeUnload({
    form,
    isSubmitting,
  })

  const onSaveDraft = useCallback(
    async (draftSubmission: submissionService.NewDraftSubmission) => {
      setDraftState({ isSavingDraft: true })
      try {
        const { submission, definition } = draftSubmission

        if (
          existingTrackingCode &&
          (await fetchIsDraftInApprovals(existingTrackingCode))
        ) {
          throw new Error('Draft has already been submitted')
        }
        if (submission.q55_question === 'No') {
          try {
            await serverValidation(
              definition.serverValidation,
              submission,
              definition,
              true,
            )
          } catch (e) {
            if (e instanceof OneBlinkAppsError) {
              setDraftState({
                isSavingDraft: false,
                invalidDraftMessage: e.message,
                draftSubmission,
              })
              return
            }
            setDraftState({ isSavingDraft: false })
            setSaveDraftError(e as Error)
            return
          }

          const trackingCode = await saveDraft(draftSubmission)

          const newElements = removeInfoElements(
            draftSubmission.definition.elements,
          )
          draftSubmission.definition.elements = newElements

          const { email } = await postRequest<{
            trackingCode: string
            email: string
          }>({
            url: `${config.HOSTED_API_URL}/activity-report/draft-email`,
            resource: {
              submission,
              definition: draftSubmission.definition,
              trackingCode,
            },
            addAuthHeaders: true,
          })

          await autoSaveService.deleteAutoSaveData(form.id, autoSaveKey)
          history.push({
            pathname: `/activity-report/draft`,
            search: `?email=${email}&trackingCode=${trackingCode}`,
          })
        } else {
          standardSaveDraft(draftSubmission)
        }
      } catch (e) {
        console.error(e)
        setSaveDraftError(e as Error)
      } finally {
        setDraftState((currentState) => ({
          ...currentState,
          isSavingDraft: false,
        }))
      }
    },
    [
      setSaveDraftError,
      history,
      standardSaveDraft,
      form.id,
      autoSaveKey,
      existingTrackingCode,
      fetchIsDraftInApprovals,
    ],
  )

  if (cancelError) {
    return (
      <ErrorMessage>
        The form has been cancelled. Please close your browser window.
      </ErrorMessage>
    )
  }

  return (
    <>
      <OneBlinkAutoSaveForm
        audience="SUBMITTER"
        form={form}
        onCancel={onCancel}
        onSubmit={onSubmit}
        onSaveDraft={onSaveDraft}
        onBeforeUnload={onBeforeUnload}
        googleMapsApiKey={config.GOOGLE_MAP_API_KEY}
        captchaType={recaptchaKeyType}
        captchaSiteKey={recaptchaPublicKey}
        abnLookupAuthenticationGuid={config.ABN_AUTH_GUID}
        initialSubmission={preFillData}
        buttons={buttons}
        disabled={isSubmitting}
        autoSaveKey={autoSaveKey}
        removeAutoSaveDataBeforeSaveDraft={false}
        removeAutoSaveDataBeforeSubmit={false}
        isPendingQueueEnabled={false}
      />

      <Dialog isOpen={isSubmitting}>
        <LoadingSpinner>Submitting</LoadingSpinner>
      </Dialog>

      <Dialog isOpen={isSavingDraft || standardIsSavingDraft || isFetching}>
        <LoadingSpinner>Saving Draft</LoadingSpinner>
      </Dialog>

      <Dialog isOpen={!!invalidDraftMessage}>
        <DialogHeading>
          <DialogHeadingText>Invalid Draft</DialogHeadingText>
        </DialogHeading>
        <div>{invalidDraftMessage}</div>
        <ButtonContainer>
          {draftSubmission && (
            <button
              className="button ob-button is-light"
              onClick={() => {
                setDraftState({
                  isSavingDraft: false,
                  draftSubmission: draftSubmission,
                })
                standardSaveDraft(draftSubmission)
              }}
            >
              <span>Save anyway</span>
            </button>
          )}
          <button
            className="button ob-button is-primary"
            style={{ marginLeft: '1rem' }}
            onClick={() => setDraftState({ isSavingDraft: false })}
          >
            <span>Return to form</span>
          </button>
        </ButtonContainer>
      </Dialog>

      <Dialog isOpen={!!saveDraftError}>
        <DialogHeading>
          <DialogHeadingText>Save draft error</DialogHeadingText>
        </DialogHeading>
        <ErrorMessage>{saveDraftError?.message}</ErrorMessage>

        <ButtonContainer>
          <button
            className="button ob-button is-primary"
            style={{ marginLeft: '1rem' }}
            onClick={() => setSaveDraftError(undefined)}
          >
            <span>Return to form</span>
          </button>
        </ButtonContainer>
      </Dialog>

      <Dialog isOpen={!!serverValidationMessage}>
        <DialogHeading>
          <DialogHeadingText>We could not submit your form</DialogHeadingText>
          <CloseIcon onClick={clearServerValidationMessage} />
        </DialogHeading>
        {serverValidationMessage}
      </Dialog>
    </>
  )
}

export default ActivityReport
